import { box, type Box } from '../geometry/box';
import { containRect, type Size } from '../geometry/framing';
import { boxAtTime, type Track, type TrackSample } from '../tracking/track';
import { normalizeRanges, range, rateOf, subtractRanges, TIME_EPSILON, type PlayRange, type TimeRange } from '../time/ranges';

/**
 * The edit as a row of clips played one after the other. Each clip shows a
 * stretch (`in`–`out`, the asset's own seconds) of one imported video, so a
 * video can be cut into pieces, joined with others and put in any order.
 *
 * Everything the editor knows about time ("source time") is time on this row.
 * What analyses find (faces, sound, GPS) belongs to the asset it was found in
 * and is placed onto the row through the clips, so reordering never re-runs
 * an analysis; what the person made on the row (cuts, captions, overlays)
 * moves with its clip when the row changes ({@link remapTime}).
 */
export type AssetId = string;

/** The video the editor was opened with: its frame is the edit's frame. */
export const PRIMARY_ASSET: AssetId = 'main';

/** How a clip whose shape differs from the edit's frame fills it. */
export type ClipFit = 'contain' | 'cover';
export const CLIP_FITS: readonly ClipFit[] = ['contain', 'cover'];

/** Clips can't be split or trimmed shorter than this. */
export const MIN_CLIP_SEC = 0.25;

export interface Clip {
  readonly id: string;
  readonly assetId: AssetId;
  /** Asset seconds where the clip starts (inclusive) and ends (exclusive). */
  readonly in: number;
  readonly out: number;
  readonly fit: ClipFit;
}

/** What a layout needs to know about an asset. */
export interface ClipSource {
  readonly durationSec: number;
  readonly width: number;
  readonly height: number;
}

export type SourceLookup = (assetId: AssetId) => ClipSource | undefined;

/** A clip at its place on the row. */
export interface PlacedClip extends Clip {
  readonly index: number;
  /** Row seconds the clip covers, `[start, end)`. */
  readonly start: number;
  readonly end: number;
  /** Where its picture sits in the frame (normalized; larger than the frame when it is cropped to fill it). */
  readonly rect: Box;
}

export interface SequenceLayout {
  readonly clips: readonly PlacedClip[];
  readonly duration: number;
  readonly frame: Size;
}

export const FULL_RECT: Box = box(0, 0, 1, 1);

export const clipLength = (clip: Pick<Clip, 'in' | 'out'>): number => Math.max(0, clip.out - clip.in);

export const wholeClip = (assetId: AssetId, durationSec: number, id = `clip-${assetId}`): Clip => ({ id, assetId, in: 0, out: durationSec, fit: 'contain' });

/** The clips of an edit; an untouched edit is just the opened video, whole. */
export function effectiveClips(clips: readonly Clip[], primary: ClipSource | undefined): readonly Clip[] {
  if (clips.length > 0 || !primary) return clips;
  return [wholeClip(PRIMARY_ASSET, primary.durationSec)];
}

/** Where a picture of `source`'s shape sits in `frame` (normalized), whole (`contain`) or filling it (`cover`). */
export function fitRect(source: Size, frame: Size, fit: ClipFit): Box {
  const sourceAspect = source.width / source.height;
  const frameAspect = frame.width / frame.height;
  if (!(sourceAspect > 0) || !(frameAspect > 0) || Math.abs(sourceAspect - frameAspect) < 0.01) return FULL_RECT;
  if (fit === 'contain') {
    const rect = containRect({ width: 1, height: 1 / frameAspect }, sourceAspect);
    return box(rect.x, rect.y * frameAspect, rect.w, rect.h * frameAspect);
  }
  return sourceAspect > frameAspect
    ? box((1 - sourceAspect / frameAspect) / 2, 0, sourceAspect / frameAspect, 1)
    : box(0, (1 - frameAspect / sourceAspect) / 2, 1, frameAspect / sourceAspect);
}

export const isFullRect = (rect: Box): boolean =>
  Math.abs(rect.x) < 1e-6 && Math.abs(rect.y) < 1e-6 && Math.abs(rect.w - 1) < 1e-6 && Math.abs(rect.h - 1) < 1e-6;

/** A box in the asset's own picture → the same spot in the frame. */
export const placeBox = (b: Box, rect: Box): Box => box(rect.x + b.x * rect.w, rect.y + b.y * rect.h, b.w * rect.w, b.h * rect.h);

/** A box in the frame → the same spot in the asset's picture. */
export const unplaceBox = (b: Box, rect: Box): Box => box((b.x - rect.x) / rect.w, (b.y - rect.y) / rect.h, b.w / rect.w, b.h / rect.h);

/**
 * Places clips one after the other. Clips whose asset is unknown are left out;
 * `in`/`out` are kept inside the asset.
 */
export function layoutSequence(clips: readonly Clip[], lookup: SourceLookup, frame: Size): SequenceLayout {
  const placed: PlacedClip[] = [];
  let position = 0;
  for (const clip of clips) {
    const source = lookup(clip.assetId);
    if (!source || !(source.durationSec > 0)) continue;
    const start = Math.min(Math.max(0, clip.in), source.durationSec);
    const end = Math.min(Math.max(start, clip.out), source.durationSec);
    if (end - start <= TIME_EPSILON) continue;
    placed.push({ ...clip, in: start, out: end, index: placed.length, start: position, end: position + end - start, rect: fitRect(source, frame, clip.fit) });
    position += end - start;
  }
  return { clips: placed, duration: position, frame };
}

/** The clip playing at row time `t`; the end of the row belongs to the last clip. */
export function clipAt(layout: SequenceLayout, t: number): PlacedClip | null {
  const { clips } = layout;
  if (clips.length === 0) return null;
  let lo = 0;
  let hi = clips.length - 1;
  while (lo < hi) {
    const mid = (lo + hi + 1) >> 1;
    if (clips[mid]!.start <= t + TIME_EPSILON) lo = mid;
    else hi = mid - 1;
  }
  return clips[lo]!;
}

/** Row time → the asset (and its own second) showing then. */
export function assetTimeAt(layout: SequenceLayout, t: number): { readonly clip: PlacedClip; readonly time: number } | null {
  const clip = clipAt(layout, t);
  if (!clip) return null;
  return { clip, time: clip.in + Math.min(Math.max(0, t - clip.start), clip.end - clip.start) };
}

/** An asset second inside `clip` → row time. */
export const rowTime = (clip: PlacedClip, assetTime: number): number => clip.start + (assetTime - clip.in);

/** Row time of the asset second `assetTime` of `assetId`, when one of its clips shows it. */
export function placeTime(layout: SequenceLayout, assetId: AssetId, assetTime: number): number | null {
  const clip = layout.clips.find((candidate) => candidate.assetId === assetId && assetTime >= candidate.in - TIME_EPSILON && assetTime < candidate.out);
  return clip ? rowTime(clip, assetTime) : null;
}

/** Ranges in an asset's own time → where its clips show them on the row. */
export function placeRanges(layout: SequenceLayout, assetId: AssetId, ranges: readonly TimeRange[]): TimeRange[] {
  const placed: TimeRange[] = [];
  for (const clip of layout.clips) {
    if (clip.assetId !== assetId) continue;
    for (const r of ranges) {
      const start = Math.max(r.start, clip.in);
      const end = Math.min(r.end, clip.out);
      if (end - start > TIME_EPSILON) placed.push(range(rowTime(clip, start), rowTime(clip, end)));
    }
  }
  return normalizeRanges(placed);
}

/** One stretch of the row that a single clip plays: what the exporter decodes. */
export interface ClipPiece {
  readonly clip: PlacedClip;
  /** Asset seconds to read, `[assetStart, assetEnd)`. */
  readonly assetStart: number;
  readonly assetEnd: number;
  /** Row time where the piece starts. */
  readonly start: number;
  /** How many times as fast it plays (1 = normal). */
  readonly rate: number;
}

/**
 * Splits row ranges at clip edges, in row order. Touching ranges stay apart:
 * each keeps its own speed.
 */
export function clipPieces(layout: SequenceLayout, ranges: readonly PlayRange[]): ClipPiece[] {
  const pieces: ClipPiece[] = [];
  const sorted = ranges.filter((r) => r.end - r.start > TIME_EPSILON).sort((a, b) => a.start - b.start);
  for (const r of sorted) {
    for (const clip of layout.clips) {
      const start = Math.max(r.start, clip.start);
      const end = Math.min(r.end, clip.end);
      if (end - start <= TIME_EPSILON) continue;
      pieces.push({ clip, start, assetStart: clip.in + (start - clip.start), assetEnd: clip.in + (end - clip.start), rate: rateOf(r) });
    }
  }
  return pieces;
}

/**
 * The asset seconds of `assetId` that the finished edit shows: the parts of
 * its clips that `keep` (row ranges) leaves in. What an analysis that only
 * matters for the result (finding faces) needs to look at.
 */
export function keptAssetRanges(layout: SequenceLayout, keep: readonly TimeRange[], assetId: AssetId): TimeRange[] {
  return normalizeRanges(clipPieces(layout, keep).filter((piece) => piece.clip.assetId === assetId).map((piece) => range(piece.assetStart, piece.assetEnd)));
}

/** Output seconds a piece lasts. */
export const pieceLength = (piece: ClipPiece): number => (piece.assetEnd - piece.assetStart) / piece.rate;

/**
 * The pieces playing output seconds `[from, to)`, cut to exactly that stretch,
 * and the output second the first of them starts at (one frame of the edit,
 * for a still picture).
 */
export function piecesBetween(pieces: readonly ClipPiece[], from: number, to: number): { readonly pieces: ClipPiece[]; readonly offset: number } {
  const cut: ClipPiece[] = [];
  let offset = 0;
  let first: number | null = null;
  for (const piece of pieces) {
    const length = pieceLength(piece);
    const start = Math.max(offset, from);
    const end = Math.min(offset + length, to);
    if (end - start > TIME_EPSILON) {
      const into = (start - offset) * piece.rate;
      const until = (end - offset) * piece.rate;
      cut.push({ ...piece, start: piece.start + into, assetStart: piece.assetStart + into, assetEnd: piece.assetStart + until });
      first ??= start;
    }
    offset += length;
    if (offset >= to) break;
  }
  return { pieces: cut, offset: first ?? 0 };
}

/** A track cut to one clip and placed on the row and in the frame. */
export interface PlacedTrack extends Track {
  readonly clipId: string;
}

/**
 * What was found in each asset, placed onto the row: every clip of an asset
 * gets its own copy of the tracks it shows, cut exactly at the clip's edges.
 * Track ids stay those of the asset, so hiding or showing a person applies to
 * every clip they appear in.
 */
export function placeTracks(layout: SequenceLayout, tracksOf: (assetId: AssetId) => readonly Track[]): PlacedTrack[] {
  const placed: PlacedTrack[] = [];
  for (const clip of layout.clips) {
    const full = isFullRect(clip.rect);
    for (const track of tracksOf(clip.assetId)) {
      const samples = cutSamples(track, clip.in, clip.out);
      if (samples.length === 0) continue;
      placed.push({
        ...track,
        clipId: clip.id,
        within: { start: clip.start, end: clip.end },
        samples: samples.map((sample) => ({ ...sample, t: rowTime(clip, sample.t), box: full ? sample.box : placeBox(sample.box, clip.rect) })),
      });
    }
  }
  return placed;
}

/** Samples inside `[from, to]`, with the track's position right at the edges when it runs across them. */
function cutSamples(track: Track, from: number, to: number): TrackSample[] {
  const inside = track.samples.filter((sample) => sample.t >= from && sample.t <= to);
  const first = track.samples[0]!;
  const last = track.samples.at(-1)!;
  if (first.t > to || last.t < from) return [];
  const edge = (t: number): TrackSample[] => {
    if (inside.some((sample) => Math.abs(sample.t - t) < TIME_EPSILON)) return [];
    const at = boxAtTime({ ...track, within: undefined }, t);
    return at ? [{ t, box: at, score: 1 }] : [];
  };
  return [...(first.t < from ? edge(from) : []), ...inside, ...(last.t > to ? edge(to) : [])];
}

// ——— Changing the row ———

/** Splits the clip under `t` in two; null when `t` is too close to a clip edge to make two real clips. */
export function splitClipAt(layout: SequenceLayout, t: number, newId: string): Clip[] | null {
  const clip = clipAt(layout, t);
  if (!clip || t - clip.start < MIN_CLIP_SEC || clip.end - t < MIN_CLIP_SEC) return null;
  const at = clip.in + (t - clip.start);
  return layout.clips.flatMap((candidate) =>
    candidate.id === clip.id ? [{ ...toClip(candidate), out: at }, { ...toClip(candidate), id: newId, in: at }] : [toClip(candidate)],
  );
}

/**
 * Puts clips in at row time `t`: between two clips when `t` is near an edge,
 * otherwise the clip under it is split and they go in between its halves.
 */
export function insertClipsAt(layout: SequenceLayout, t: number, added: readonly Clip[], splitId: string): Clip[] {
  const clips = layout.clips.map(toClip);
  if (layout.clips.length === 0) return [...added];
  const clip = clipAt(layout, t)!;
  if (t - clip.start < MIN_CLIP_SEC) return [...clips.slice(0, clip.index), ...added, ...clips.slice(clip.index)];
  if (clip.end - t < MIN_CLIP_SEC) return [...clips.slice(0, clip.index + 1), ...added, ...clips.slice(clip.index + 1)];
  const split = splitClipAt(layout, t, splitId)!;
  return [...split.slice(0, clip.index + 1), ...added, ...split.slice(clip.index + 1)];
}

/** Moves a clip to another place in the row (`index` among the clips after it was taken out). */
export function moveClip(clips: readonly Clip[], id: string, index: number): Clip[] {
  const from = clips.findIndex((clip) => clip.id === id);
  if (from === -1) return [...clips];
  const rest = clips.filter((clip) => clip.id !== id);
  const to = Math.min(Math.max(0, index), rest.length);
  return [...rest.slice(0, to), clips[from]!, ...rest.slice(to)];
}

/** Takes a clip out of the row; the last clip always stays (an edit without video can't play or export). */
export function removeClip(clips: readonly Clip[], id: string): Clip[] {
  return clips.length <= 1 ? [...clips] : clips.filter((clip) => clip.id !== id);
}

export const updateClip = (clips: readonly Clip[], id: string, patch: Partial<Omit<Clip, 'id' | 'assetId'>>): Clip[] =>
  clips.map((clip) => (clip.id === id ? { ...clip, ...patch } : clip));

const toClip = ({ id, assetId, in: start, out, fit }: Clip): Clip => ({ id, assetId, in: start, out, fit });

// ——— Moving what was made on the row along with its clips ———

/**
 * Which clips after a change take over a clip from before: the same clip, and
 * pieces that are new (the other half of a split). A new piece only takes the
 * moments the same clip no longer shows, so a second copy of a video added
 * later starts clean. Moments of a clip that was taken out go with it.
 */
function successors(before: SequenceLayout, after: SequenceLayout, clip: PlacedClip): { readonly same: PlacedClip | undefined; readonly pieces: readonly PlacedClip[] } {
  const known = new Set(before.clips.map((candidate) => candidate.id));
  return {
    same: after.clips.find((candidate) => candidate.id === clip.id && candidate.assetId === clip.assetId),
    pieces: after.clips.filter((candidate) => candidate.assetId === clip.assetId && !known.has(candidate.id)),
  };
}

const shows = (clip: PlacedClip, assetTime: number) => assetTime >= clip.in - TIME_EPSILON && assetTime <= clip.out + TIME_EPSILON;

/** Where the moment at row time `t` is after the row changed; null when its clip was taken out. */
export function remapTime(before: SequenceLayout, after: SequenceLayout, t: number): number | null {
  const located = assetTimeAt(before, t);
  if (!located) return null;
  const { same, pieces } = successors(before, after, located.clip);
  const target = same && shows(same, located.time) ? same : pieces.find((piece) => shows(piece, located.time));
  return target ? rowTime(target, Math.min(Math.max(located.time, target.in), target.out)) : null;
}

/** Ranges on the row after it changed: each part follows its clip, parts of clips taken out are gone. */
export function remapRanges(before: SequenceLayout, after: SequenceLayout, ranges: readonly TimeRange[]): TimeRange[] {
  const result: TimeRange[] = [];
  const onto = (target: PlacedClip, part: TimeRange) => {
    const start = Math.max(part.start, target.in);
    const end = Math.min(part.end, target.out);
    if (end - start > TIME_EPSILON) result.push(range(rowTime(target, start), rowTime(target, end)));
  };
  for (const piece of clipPieces(before, ranges)) {
    const { same, pieces } = successors(before, after, piece.clip);
    const part = range(piece.assetStart, piece.assetEnd);
    if (same) onto(same, part);
    const left = same ? subtractRanges([part], [range(same.in, same.out)]) : [part];
    for (const rest of left) for (const target of pieces) onto(target, rest);
  }
  return normalizeRanges(result);
}

/** True when two layouts play the same clips in the same order over the same stretches. */
export function sameRow(a: SequenceLayout, b: SequenceLayout): boolean {
  return (
    a.clips.length === b.clips.length &&
    a.clips.every((clip, index) => {
      const other = b.clips[index]!;
      return clip.assetId === other.assetId && Math.abs(clip.in - other.in) < TIME_EPSILON && Math.abs(clip.out - other.out) < TIME_EPSILON;
    })
  );
}
